import { BarChart3, ClipboardList, Settings, Table2, Tags, Workflow, type LucideIcon } from 'lucide-react'
import { useLocation, useParams } from 'react-router-dom'

import { useAllowedActions } from '@/hooks/usePermission'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { RecordGroup } from '@/types/api/record-group'

export type GroupSectionId = 'records' | 'form' | 'automations' | 'tags' | 'dashboard' | 'settings'

export interface GroupSection {
  value: GroupSectionId
  label: string
  icon: LucideIcon
  to: string
  active: boolean
}

const SECTIONS: Array<{ value: GroupSectionId; label: string; icon: LucideIcon }> = [
  { value: 'records', label: 'Records', icon: Table2 },
  { value: 'form', label: 'Form Designer', icon: ClipboardList },
  { value: 'automations', label: 'Automations', icon: Workflow },
  { value: 'tags', label: 'Tags', icon: Tags },
  { value: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { value: 'settings', label: 'Settings', icon: Settings },
]

/** The sections of a record group the user may open, with links and the active one (state lives in the URL). */
export function useGroupSections(group: RecordGroup | null): GroupSection[] {
  const { recordGroupId } = useParams<{ recordGroupId: string }>()
  const { pathname, search } = useLocation()
  const actions = useAllowedActions('RECORD_GROUP', recordGroupId)
  const recordTab = new URLSearchParams(search).get('rtab') ?? 'records'
  const groupPath = `/record-groups/${recordGroupId}`
  const onForm = pathname.endsWith('/form')
  const canDesignForm = actions.can(PERMISSION_KEYS.FORM_CREATE) || actions.can(PERMISSION_KEYS.FORM_UPDATE)

  return SECTIONS.filter(({ value }) => {
    if (value === 'form') return canDesignForm
    if (value === 'automations') return actions.can(PERMISSION_KEYS.AUTOMATION_VIEW)
    if (value === 'tags' || value === 'dashboard' || value === 'settings') return !group?.traceOnly
    return true
  }).map((section) => ({
    ...section,
    to: section.value === 'form' ? `${groupPath}/form` : section.value === 'records' ? groupPath : `${groupPath}?rtab=${section.value}`,
    active: section.value === 'form' ? onForm : !onForm && recordTab === section.value,
  }))
}
